import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import { getRandomPastelColor, formatName, convertTimeToDays } from './NotificationUtils';
import { getNotificationIcon } from './NotificationItemIcons';

const NotificationItem = ({
  notification,
  index,
  handlers,
  setOpenRightPanel,
  markNotificationAsRead,
}) => {
  const {
    goToPatient,
    handleAttachmentOpen,
    handleViewGoal,
    handleVideoOpen,
    navigateToChatScreen,
    openDescriptionModel,
  } = handlers;

  const userName =
    notification?.userName || notification?.name || 'Unknown User';
  const firstName = userName.split(' ')[0];
  const firstLetter = firstName.charAt(0).toUpperCase();

  return (
    <Card
      key={index}
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: !notification.readStatus
          ? '2px solid #9291ef'
          : '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {!notification.readStatus && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 8,
            height: 8,
            backgroundColor: '#9291ef',
            borderRadius: '50%',
            zIndex: 1,
          }}
        />
      )}
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2} alignItems='flex-start'>
          <Grid item>
            <Avatar
              sx={{
                height: 50,
                width: 50,
                bgcolor: getRandomPastelColor(userName),
                color: '#000',
                fontWeight: 600,
                fontSize: '1.2rem',
                border: '2px solid #e2e8f0',
              }}
            >
              {firstLetter}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              {getNotificationIcon(notification?.notificationSource)}
              <Typography
                variant='h6'
                sx={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  color: '#1e293b',
                  flex: 1,
                }}
              >
                {formatName(userName)}
              </Typography>
            </Box>

            <Typography
              variant='body2'
              sx={{
                color: '#64748b',
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {notification.title || 'No title available'}
            </Typography>

            {/* Action Links */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              {notification?.notificationSource === 'newPatientReg' && (
                <Typography
                  component='span'
                  sx={{
                    color: '#9291ef',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    goToPatient(notification.userId);
                    markNotificationAsRead(notification.id);
                  }}
                >
                  View profile
                </Typography>
              )}

              {(notification?.notificationSource === 'patientVideoUploaded' ||
                notification?.notificationSource === 'doctorVideoUploaded' ||
                notification?.notificationSource === 'teacherVideoUploaded' ||
                notification?.notificationSource === 'studentVideoUploaded' ||
                notification?.notificationSource === 'uploadVideo') && (
                <Typography
                  component='span'
                  sx={{
                    color: '#9291ef',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    handleVideoOpen(notification);
                    setOpenRightPanel(false);
                    markNotificationAsRead(notification.id);
                  }}
                >
                  View video
                </Typography>
              )}

              {(notification?.notificationSource ===
                'patientDocumentUploaded' ||
                notification?.notificationSource === 'doctorDocumentUploaded' ||
                notification?.notificationSource ===
                  'teacherDocumentUploaded' ||
                notification?.notificationSource ===
                  'studentDocumentUploaded' ||
                notification?.notificationSource ===
                  'patientAttachmentUploaded' ||
                notification?.notificationSource ===
                  'doctorAttachmentUploaded' ||
                notification?.notificationSource ===
                  'teacherAttachmentUploaded' ||
                notification?.notificationSource ===
                  'studentAttachmentUploaded') && (
                <Typography
                  component='span'
                  sx={{
                    color: '#9291ef',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    handleAttachmentOpen(notification);
                  }}
                >
                  View attachment
                </Typography>
              )}

              {(notification?.notificationSource === 'patientMessage' ||
                notification?.notificationSource === 'doctorMessage' ||
                notification?.notificationSource === 'studentMessage' ||
                notification?.notificationSource === 'teacherMessage' ||
                notification?.notificationSource === 'groupMessage') && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      color: '#1e293b',
                    }}
                  >
                    {notification?.description || 'No description'}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{
                      color: '#9291ef',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => {
                      navigateToChatScreen(notification);
                      openDescriptionModel();
                      // Don't mark as read here - let UnreadCount handle it when chat opens
                    }}
                  >
                    View message
                  </Typography>
                </Box>
              )}

              {(notification?.notificationSource === 'patientGoalCreated' ||
                notification?.notificationSource === 'doctorGoalCreated' ||
                notification?.notificationSource === 'teacherGoalCreated' ||
                notification?.notificationSource === 'studentGoalCreated') && (
                <Typography
                  component='span'
                  sx={{
                    color: '#9291ef',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    handleViewGoal(notification);
                  }}
                >
                  View goal
                </Typography>
              )}

              {(notification?.notificationSource === 'patientBehaviorCreated' ||
                notification?.notificationSource === 'doctorBehaviorCreated' ||
                notification?.notificationSource === 'teacherBehaviorCreated' ||
                notification?.notificationSource ===
                  'studentBehaviorCreated') && (
                <Typography
                  component='span'
                  sx={{
                    color: '#9291ef',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    handleViewGoal(notification);
                  }}
                >
                  View behavior
                </Typography>
              )}

              {notification.notificationSource === 'uploadVideo' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      color: '#1e293b',
                    }}
                  >
                    {notification?.description || 'No description'}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{
                      color: '#9291ef',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => {
                      goToPatient(notification.userId || notification?.id);
                      markNotificationAsRead(notification.id);
                    }}
                  >
                    Please review
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Chip
              label={`${convertTimeToDays(notification.createdDate)} days ago`}
              size='small'
              sx={{
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NotificationItem;
